import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Upload, FileText, X, AlertCircle, CheckCircle, AwardIcon } from 'lucide-react';
import { Notification } from '../../../../../components/ui/custom-notification';
import { processSpecificationUpload } from '../../../../../lib/specificationUtils';
import * as XLSX from 'xlsx';
import { AddSpecification, getPreSignedUrlToUpload } from '../../../../../redux/Project/specificationSaga';
import { SPEC_TYPE_KEYS } from '../../../constants/specificationConstants';

interface UploadSpecificationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
  setupData: any;
}

export const UploadSpecificationsDialog: React.FC<UploadSpecificationsDialogProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  setupData
}) => {
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [excelData, setExcelData] = useState<any[]>([]);
  const [matchedDocuments, setMatchedDocuments] = useState<any[]>([]);
  const [isProcessingExcel, setIsProcessingExcel] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}')


  // Reset all fields when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal closes
      setExcelFile(null);
      setDocumentFiles([]);
      setExcelData([]);
      setMatchedDocuments([]);
      setIsUploading(false);
      setIsProcessingExcel(false);
    }
  }, [isOpen]);

  const handleExcelFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      // Check if it's an Excel file
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-excel' ||
        file.name.endsWith('.xlsx') ||
        file.name.endsWith('.xls')) {
        setExcelFile(file);
        processExcelFile(file);
        Notification.success('Excel template uploaded successfully');
      } else {
        Notification.error('Please upload an Excel file (.xlsx or .xls)');
      }
    }
  };

  const processExcelFile = async (file: File) => {
    setIsProcessingExcel(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const sheetName = workbook.SheetNames[1];
      const worksheet = workbook.Sheets['Template'];
      const data = XLSX.utils.sheet_to_json(worksheet);

      const convertedData = data.map((row: any) => {
        return {
          fileName: row['File Names'],
          pipeSelection: row['Pipe Selection'],
          specificationType: row['Specification Type'],
          projectType: row['Project Type'],
          //   pipeId: row['Pipe ID'] || row['PipeId'] || row['pipe_id'],
          //   projectId: row['Project ID'] || row['ProjectId'] || row['project_id'],
          //   projectTypeId: row['Project Type ID'] || row['ProjectTypeId'] || row['project_type_id'],
          //   type: row['Type'] || row['type'],
          //   details: {
          //     description: row['Description'] || row['description'],
          //     notes: row['Notes'] || row['notes']
          //   }
        };
      });
      setExcelData(convertedData);
      // Match documents with Excel data
      matchDocumentsWithExcel(convertedData, documentFiles);

    } catch (error) {
      Notification.error('Failed to process Excel file');
    } finally {
      setIsProcessingExcel(false);
    }
  };

  const matchDocumentsWithExcel = (excelData: any[], documentFiles: any[]) => {

    const matched = excelData.map((row: any) => {
      // Clean the fileName from Excel (remove quotes, trim spaces)
      const cleanFileName = row.fileName?.replace(/['"]/g, '').trim();
      const specificationType = SPEC_TYPE_KEYS[row.specificationType]
   
      // Split comma-separated filenames
      let expectedFileNames = cleanFileName?.split(',').map((name: string) => name.trim()) || [];
      // Remove the restriction that limits configuration setups to only first file
      // Allow multiple files for all specification types
      // if(specificationType && isConfigurationSetup(specificationType) && expectedFileNames.length > 0) {
      //   expectedFileNames = [expectedFileNames[0]]
      // }

      // Find matching files for each expected filename
      const matchedFiles = expectedFileNames.map((expectedFileName: string) => {
        const matchingFile = documentFiles.find(file => {
          const fileName = file.name.toLowerCase();
          const excelFileName = expectedFileName.toLowerCase();
        

          return fileName === excelFileName ||
            fileName.includes(excelFileName || '') ||
            excelFileName?.includes(fileName);
        });

        return matchingFile;
      }).filter(Boolean); // Remove undefined values
      
   
   
      return {
        ...row,
        matchedFiles:matchedFiles,
        expectedFileNames:  expectedFileNames,
        isMatched: matchedFiles.length > 0,
        isFullyMatched: matchedFiles.length === expectedFileNames.length
      };
    });

    setMatchedDocuments(matched);
  };

  const isConfigurationSetup = (type:string) => {
    return type !== 'FITMENT_READINGS' && type !== 'PASS_CONFIG'
                  && type !== 'WELD_PROCESS' && type !== 'ALIGNMENT';
  };

  const handleDocumentFilesSelect = (files: FileList | null) => {

    if (files) {
      const newFiles = Array.from(files);

      const updatedFiles = [...documentFiles, ...newFiles];

      setDocumentFiles(updatedFiles);

      // Re-match documents if Excel data exists
      if (excelData.length > 0) {
        matchDocumentsWithExcel(excelData, updatedFiles);
      }
    } else {
    }
  };

  const handleRemoveExcelFile = () => {
    setExcelFile(null);
    setDocumentFiles([]); // Automatically remove all uploaded documents
    setExcelData([]);
    setMatchedDocuments([]);
    setIsProcessingExcel(false);
    Notification.success('Excel template and documents removed successfully');
  };

  const handleRemoveDocumentFile = (index: number) => {
    const updatedFiles = documentFiles.filter((_, i) => i !== index);
    setDocumentFiles(updatedFiles);

    // Re-match documents if Excel data exists
    if (excelData.length > 0) {
      matchDocumentsWithExcel(excelData, updatedFiles);
    }
  };

  const isFabricationSetup = () => {
    if (!setupData || typeof setupData !== 'object') return false;
    return Object.values(setupData).some((setup: any) => setup &&
      (setup.projectType === 'fabrication'));
  };

  const handleUpload = async () => {
    if (!excelFile) {
      Notification.error('Please upload an Excel template first');
      return;
    }
    
    // Check if there are any specifications with filenames that need documents
    const specificationsWithFiles = matchedDocuments.filter(spec => 
      spec.fileName && spec.fileName.trim() !== ''
    );
    
    if (specificationsWithFiles.length > 0 && documentFiles.length === 0) {
      Notification.error('Please upload at least one document for specifications with filenames');
      return;
    }

    if (excelData.length === 0) {
      Notification.error('No data found in Excel file');
      return;
    }

    setIsUploading(true);
    const newDoc: any[] = [];
    try {
      // Process each matched specification
      for (const matchedSpec of matchedDocuments) {
        const hasFileName = matchedSpec.fileName && matchedSpec.fileName.trim() !== '';
        
        // Skip if no filename (empty filename is allowed)
        if (!hasFileName) {
          continue;
        }
        
        if (matchedSpec.isMatched && matchedSpec.matchedFiles && matchedSpec.matchedFiles.length > 0) {
          // Determine projectId and projectTypeId based on setup type
          let projectId, projectTypeId;
          if (matchedSpec.projectType === 'Buckle Arrestor' || matchedSpec.projectType === 'Pipe In Pipe' ||
            matchedSpec.projectType === 'BulkHead') {
            const type = matchedSpec.projectType === 'Buckle Arrestor' ? 'buckleArrestor' : matchedSpec.projectType === 'Pipe In Pipe' ? 'pipeInPipe' : 'bulkHead'
            
            // For fabrication projects, find the fabrication setup data
            const setupDataValues = Object.values(setupData);
            const fabricationSetupData = setupDataValues.find((setup: any) =>
              setup && setup.projectType === 'fabrication' && setup.projectSubType === type
            ) as any;
            
            projectId = fabricationSetupData?.projectId;
            projectTypeId = fabricationSetupData?.projectTypeId;

          } else if (matchedSpec.projectType === 'Others') {
            // For others projects, find the others setup data
            const othersSetupData = Object.values(setupData).find((setup: any) => setup.projectType === 'others') as any;
            projectId = othersSetupData?.projectId;
            projectTypeId = othersSetupData?.projectTypeId;

          } else {
            // For pipeline projects, find the pipeline setup data
            const setupDataValues = Object.values(setupData);
            const pipelineSetupData = setupDataValues.find((setup: any) => 
              setup && setup.projectType === 'pipeline'
            ) as any;
            
            projectId = pipelineSetupData?.projectId;
            projectTypeId = pipelineSetupData?.projectTypeId;
          }

          // Extract pipeline ID from pipeSelection if it contains "Pipeline - [ID]"
          let pipelineId = null;
          if (matchedSpec.pipeSelection && matchedSpec.pipeSelection.includes(' - [')) {
            // Match any pattern like "Pipeline - [248]" or "Fabrication - [123]"
            const match = matchedSpec.pipeSelection.match(/ - \[(\d+)\]/);
            if (match) {
              pipelineId = parseInt(match[1]);
            }
          }
          const specificationType = SPEC_TYPE_KEYS[matchedSpec.specificationType]

          // Validate that we have the required IDs
          if (!projectId || !projectTypeId) {
            Notification.error(`Could not determine project information for ${matchedSpec.projectType}. Please check the data.`);
            continue;
          }

          const payload = {
            pipeId: Number(pipelineId),
            projectId: Number(projectId),
            projectTypeId: Number(projectTypeId),
            type: specificationType
          }
          // Ensure matchedFiles is always an array
          const filesToUpload = Array.isArray(matchedSpec.matchedFiles) 
            ? matchedSpec.matchedFiles 
            : [matchedSpec.matchedFiles].filter(Boolean);
          
          const s3Response = await getPreSignedUrlToUpload(filesToUpload, payload);


          // Convert to specification document format
          const specificationDoc = {
            pipeId: pipelineId || matchedSpec.pipeSelection.split(' ')[1],
            projectId: projectId,
            projectTypeId: projectTypeId,
            type: specificationType,
            files: s3Response.map((file: any) => ({
              version: file.version + 1 || 1,
              fileName: file.fileName,
              s3Key: file.s3Key
            })),
            uploadDate: new Date().toISOString(),
            uploadedBy: user.email || 'no email'
          };

          newDoc.push(specificationDoc);
          // Here you would call your API to upload the specification
          // await AddSpecification(specificationDoc, false);
        }
      }
      const response = await AddSpecification(newDoc, false)


      if (response) {
       await onUploadComplete?.();
        // Reset all fields after successful upload
        setExcelFile(null);
        setDocumentFiles([]);
        setExcelData([]);
        setMatchedDocuments([]);
        setIsUploading(false);
        setIsProcessingExcel(false);
        onClose();
      }
    } catch (error) {
      Notification.error('Failed to upload specifications. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    // Reset all state
    setExcelFile(null);
    setDocumentFiles([]);
    setExcelData([]);
    setMatchedDocuments([]);
    setIsUploading(false);
    setIsProcessingExcel(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-[760px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white">
        <div className="flex items-center gap-3 border-b border-[#E2E5EA] px-6 py-4 pr-14 flex-shrink-0">
          <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
            <Upload className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
            Upload Specifications
          </DialogTitle>
        </div>

        <div className="space-y-6 flex-1 overflow-y-auto overflow-x-hidden p-6">
          {/* Step 1: Upload Excel Template */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                1
              </div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Upload Excel Template</h3>
            </div>
            <p className="text-[13px] text-text-grey">
              Upload the Excel template file that contains the specification structure.
            </p>

            {excelFile ? (
              <div className="flex items-center p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                <FileText className="h-4 w-4 text-heritage-grey mr-2" />
                <span className="text-[13px] text-text-grey flex-1">{excelFile.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveExcelFile}
                  className="h-6 w-6 p-0 hover:bg-hero-red-10 hover:text-readable-red"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <FileUpload
                onFileSelect={handleExcelFileSelect}
                accept=".xlsx,.xls"
                multiple={false}
                buttonText="Upload Excel Template"
                variant="outline"
                size="lg"
                className="w-full"
              />
            )}
          </div>

          {/* Step 2: Upload Documents */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-hero-red-10 text-readable-red rounded-full flex items-center justify-center text-[12px] font-medium">
                2
              </div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">Upload Documents</h3>
            </div>
            <p className="text-[13px] text-text-grey">
              Upload the specification documents (PDF, DOC, etc.) that correspond to the template.
            </p>

            <div className="w-full overflow-x-hidden">
              <FileUpload
                onFileSelect={handleDocumentFilesSelect}
                accept=".pdf,.doc,.docx,.xlsx,.xls"
                multiple={true}
                maxFiles={10}
                disabled={!excelFile}
                buttonText="Upload Documents"
                variant="outline"
                size="lg"
                onRemoveFile={handleRemoveDocumentFile}
                selectedFiles={documentFiles}
                showFileList={documentFiles.length > 0}
                className="w-full"
              />
            </div>

            {/* Debug: Show current documentFiles count */}
            <div className="text-[12px] text-heritage-grey">
              Current files: {documentFiles.length} file(s)
            </div>

            {!excelFile && (
              <div className="flex items-center gap-2 p-3 bg-hero-red-10 border border-hero-red-20 rounded-lg">
                <AlertCircle className="h-4 w-4 text-hero-red" />
                <span className="text-[13px] text-readable-red">
                  Please upload an Excel template first to enable document uploads
                </span>
              </div>
            )}

            {/* Excel Processing Status */}
            {excelFile && isProcessingExcel && (
              <div className="flex items-center gap-2 p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                <div className="w-4 h-4 border-2 border-hero-red border-t-transparent rounded-full animate-spin"></div>
                <span className="text-[13px] text-text-grey">Processing Excel file...</span>
              </div>
            )}

            {/* Debug Information */}
            {/* {excelData.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Debug Information:</h4>
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-xs text-gray-600 mb-2">
                    <strong>Available Files:</strong> {documentFiles.map(f => f.name).join(', ')}
                  </div>
                  <div className="text-xs text-gray-600">
                    <strong>Excel File Names:</strong> {excelData.map(item => item.fileName).join(', ')}
                  </div>
                </div>
              </div>
            )} */}

            {/* Matched Documents Display */}
            {excelData.length > 0 && documentFiles.length > 0 && matchedDocuments.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-[13px] font-medium text-blueprint-blue">Excel Data & Document Matching:</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto overflow-x-hidden border border-[#E2E5EA] rounded-lg p-2">
                  {matchedDocuments.map((item, index) => {
                    const hasFileName = item.fileName && item.fileName.trim() !== '';
                    
                    // If filename is empty, do not render this card
                    if (!hasFileName) {
                      return null;
                    }
                    
                    return (
                      <div key={index} className={`p-3 rounded-lg border ${item.isMatched
                        ? 'bg-page-bg border-[#E2E5EA]'
                        : 'bg-[rgba(196,0,31,0.06)] border-hero-red-20'
                      }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="text-[13px] font-medium text-text-grey">
                            {item.specificationType || item.type} - {item.pipeSelection || `Pipe ${item.pipeId}`}
                          </div>
                          <div className="text-[12px] text-heritage-grey">
                            Expected: {item.expectedFileNames?.join(', ') || item.fileName || 'No filename in Excel'}
                          </div>
                          {item.isMatched && (
                            <div className="text-[12px] text-blueprint-blue flex items-center gap-1 mt-1">
                              <CheckCircle className="h-3 w-3" />
                              Matched: {item.matchedFiles?.map((f: any) => f.name).join(', ')}
                              {!item.isFullyMatched && (
                                <span className="text-readable-red ml-1">
                                  ({item.matchedFiles?.length}/{item.expectedFileNames?.length} files)
                                </span>
                              )}
                            </div>
                          )}
                          {!item.isMatched && (
                            <div className="text-[12px] text-readable-red flex items-center gap-1 mt-1">
                              <AlertCircle className="h-3 w-3" />
                              No matching documents found
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Upload Summary */}
          {excelFile && documentFiles.length > 0 && (
            <div className="p-4 bg-[rgba(0,39,63,0.06)] border border-[#E2E5EA] rounded-lg">
              <h4 className="text-[13px] font-medium text-blueprint-blue mb-2">Upload Summary</h4>
              <div className="space-y-1 text-[13px] text-text-grey">
                <div>• Excel Template: {excelFile.name}</div>
                <div>• Documents: {documentFiles.length} file(s)</div>
                <div>• Total Size: {(documentFiles.reduce((acc, file) => acc + file.size, 0) / 1024 / 1024).toFixed(2)} MB</div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 flex-shrink-0 border-t border-[#E2E5EA] bg-page-bg px-6 py-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!excelFile || isUploading}
            className="bg-hero-red hover:bg-hero-red-60 text-white min-w-[100px]"
          >
            {isUploading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Upload All
              </div>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 